/**
 * ProcedureCitationsPage — Compliance · Procedure Citations.
 *
 * COMPLIANCE-CRITICAL catalog. Every admin-configurable default row
 * (EntityRuleGateEnforcement, GateKeys, ProductServiceAvailability, …) carries
 * a NOT-NULL LegacyProcedureCitation FK that points back into this table. The
 * citation is the proof that a default value was sourced from a real PCB
 * procedure rather than guessed — enforcing CLAUDE.md canonical rule #1
 * ("DO NOT GUESS about legacy procedure"). Every gate / admin default cites one.
 *
 * Reads  /v3/config/procedure-citations (Tier-1 paired read; Record rows).
 * Writes go through the typed bearer-only admin surface (first arg '' = no
 * bypass key; the surface is bearer-authenticated):
 *   client.retail.admin.compliance.procedureCitations.upsert('', body)
 *   client.retail.admin.compliance.procedureCitations.remove('', citationKey)
 *
 * Built to match the ProductsPage CRUD pattern: reload via config read,
 * DataTable with trailing Edit / Delete ghost buttons, a top "New" Card
 * action, a ModalShell form using FormField primitives, a save() handler, a
 * confirm-gated delete, and a Banner surfaced inside the modal on save failure.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ProcedureCitationUpsertBody } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, TextArea, SelectField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

/**
 * Local wire row shape. The Tier-1 read returns `Record<string, unknown>`
 * rows; we cast each row to this interface at the reload boundary rather than
 * sprinkle `any`. Field set mirrors ProcedureCitationUpsertBody plus the PK.
 */
interface CitationRow {
  citationKey: string;
  citationType: string;
  displayLabel: string;
  sourcePath: string | null;
  pageReference: string | null;
  author: string | null;
  notes: string | null;
  sourceDate: string | null;
  isActive: boolean;
}

/**
 * citationType ENUM — the canonical 9-value source-of-truth taxonomy. EXACT
 * values per the api-client ProcedureCitationUpsertBody contract; labels
 * mirror the ROP-teams editor so the two surfaces read identically.
 */
const CITATION_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'memory-file', label: 'Memory file — project_*_procedure_full_lock_*.md' },
  { value: 'pdf-page', label: 'PDF page — Legacy Procedures PDF passage' },
  { value: 'form-id', label: 'Form ID — G:\\Forms\\* PCB internal form' },
  { value: 'collette-confirm', label: 'Collette confirmation — chat / memory note' },
  { value: 'aurora-export', label: 'Aurora export — CIF# / Acct# reference' },
  { value: 'bill-directive', label: 'Bill directive — SPEC.md anchor / chat directive' },
  { value: 'scott-bond-ticket', label: 'Scott Bond ticket — JHA support request' },
  { value: 'collette-pcb-form', label: 'Collette PCB form — Collette-validated internal form' },
  { value: 'external-reg', label: 'External reg — must pair with memory-file or pdf-page' },
];

const CITATION_TYPE_VALUES: ReadonlySet<string> = new Set(
  CITATION_TYPE_OPTIONS.map((o) => o.value),
);

interface EditDraft {
  isNew: boolean;
  citationKey: string;
  citationType: string;
  displayLabel: string;
  sourcePath: string;
  pageReference: string;
  author: string;
  notes: string;
  sourceDate: string;
  isActive: boolean;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}

function nullableStr(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

function bool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

/** Cast a raw Tier-1 config row to the local typed row shape. */
function toRow(raw: Record<string, unknown>): CitationRow {
  return {
    citationKey: str(raw.citationKey),
    citationType: str(raw.citationType),
    displayLabel: str(raw.displayLabel),
    sourcePath: nullableStr(raw.sourcePath),
    pageReference: nullableStr(raw.pageReference),
    author: nullableStr(raw.author),
    notes: nullableStr(raw.notes),
    sourceDate: nullableStr(raw.sourceDate),
    isActive: bool(raw.isActive, true),
  };
}

/** Normalize an API ISO/datetime value to the YYYY-MM-DD the date input expects. */
function toDateInput(v: string | null): string {
  if (!v) return '';
  // Accept both bare "YYYY-MM-DD" and full ISO timestamps; keep the date part.
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(v.trim());
  return m ? m[1] : v.trim();
}

function rowToDraft(r: CitationRow): EditDraft {
  return {
    isNew: false,
    citationKey: r.citationKey,
    citationType: CITATION_TYPE_VALUES.has(r.citationType) ? r.citationType : 'memory-file',
    displayLabel: r.displayLabel,
    sourcePath: r.sourcePath ?? '',
    pageReference: r.pageReference ?? '',
    author: r.author ?? '',
    notes: r.notes ?? '',
    sourceDate: toDateInput(r.sourceDate),
    isActive: r.isActive,
  };
}

function blankDraft(): EditDraft {
  return {
    isNew: true,
    citationKey: '',
    citationType: 'memory-file',
    displayLabel: '',
    sourcePath: '',
    pageReference: '',
    author: 'Bill',
    notes: '',
    sourceDate: '',
    isActive: true,
  };
}

function draftToBody(d: EditDraft): ProcedureCitationUpsertBody {
  const nullable = (s: string): string | null => {
    const t = s.trim();
    return t === '' ? null : t;
  };
  return {
    citationKey: d.citationKey.trim(),
    citationType: d.citationType,
    displayLabel: d.displayLabel.trim(),
    sourcePath: nullable(d.sourcePath),
    pageReference: nullable(d.pageReference),
    author: nullable(d.author),
    notes: nullable(d.notes),
    // Date input is already YYYY-MM-DD; send as-is (ISO date) or null.
    sourceDate: nullable(d.sourceDate),
    isActive: d.isActive,
  };
}

function typeLabel(value: string): string {
  return CITATION_TYPE_OPTIONS.find((o) => o.value === value)?.label.split(' — ')[0] ?? value;
}

export function ProcedureCitationsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<CitationRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const resp = await client.retail.config.procedureCitations();
      if (resp.success && Array.isArray(resp.data)) {
        setRows(resp.data.map(toRow));
      } else {
        setError(resp.error ?? 'Failed to load procedure citations.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    let out = rows;
    if (typeFilter) out = out.filter((r) => r.citationType === typeFilter);
    const n = filter.trim().toLowerCase();
    if (n) {
      out = out.filter(
        (r) =>
          r.citationKey.toLowerCase().includes(n) ||
          r.displayLabel.toLowerCase().includes(n) ||
          (r.sourcePath ?? '').toLowerCase().includes(n) ||
          (r.author ?? '').toLowerCase().includes(n),
      );
    }
    return out;
  }, [rows, filter, typeFilter]);

  const typeFilterOptions = useMemo(
    () => [
      { value: '', label: `All types (${rows?.length ?? 0})` },
      ...CITATION_TYPE_OPTIONS.map((t) => ({
        value: t.value,
        label: `${typeLabel(t.value)} (${rows?.filter((r) => r.citationType === t.value).length ?? 0})`,
      })),
    ],
    [rows],
  );

  const remove = useCallback(
    async (citationKey: string): Promise<void> => {
      if (
        !window.confirm(
          `Retire citation "${citationKey}"? It will be soft-deleted (isActive=false). ` +
            `Existing FK references on gates / defaults remain intact.`,
        )
      ) {
        return;
      }
      setError(null);
      try {
        const resp = await client.retail.admin.compliance.procedureCitations.remove('', citationKey);
        if (resp.success === false) {
          setError(resp.error ?? 'Delete failed.');
          return;
        }
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [client, reload],
  );

  const columns: ReadonlyArray<DataTableColumn<CitationRow>> = useMemo(
    () => [
      {
        header: 'Key',
        sortKey: (r) => r.citationKey,
        cell: (r) => <code className="cell-mono--bold">{r.citationKey}</code>,
      },
      {
        header: 'Type',
        sortKey: (r) => r.citationType,
        cell: (r) => <Pill tone="muted">{typeLabel(r.citationType)}</Pill>,
      },
      { header: 'Label', sortKey: (r) => r.displayLabel, cell: (r) => r.displayLabel },
      {
        header: 'Source',
        cell: (r) =>
          r.sourcePath ? (
            <code className="cell-mono">
              {r.sourcePath}
              {r.pageReference ? ` · ${r.pageReference}` : ''}
            </code>
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      {
        header: 'Author',
        sortKey: (r) => r.author ?? '',
        cell: (r) => r.author ?? <span className="cell-dim">—</span>,
      },
      {
        header: 'Date',
        sortKey: (r) => r.sourceDate ?? '',
        cell: (r) =>
          r.sourceDate ? (
            <span className="cell-mono">{toDateInput(r.sourceDate)}</span>
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      {
        header: 'Status',
        sortKey: (r) => (r.isActive ? 1 : 0),
        cell: (r) =>
          r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
      },
      {
        header: '',
        cell: (r) => (
          <div className="cell-actions">
            <Button size="sm" variant="ghost" onClick={() => setDraft(rowToDraft(r))}>
              Edit
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void remove(r.citationKey)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [remove],
  );

  const save = useCallback(
    async (d: EditDraft): Promise<void> => {
      // Client-side guard mirrors the NOT-NULL contract: a citation is
      // worthless without a key and a human-readable label.
      if (!d.citationKey.trim() || !d.displayLabel.trim()) {
        setSaveErr('Citation key and display label are both required.');
        return;
      }
      setSaving(true);
      setSaveErr(null);
      try {
        const body = draftToBody(d);
        const resp = await client.retail.admin.compliance.procedureCitations.upsert('', body);
        if (resp.success === false) {
          setSaveErr(resp.error ?? 'Save failed.');
          return;
        }
        setDraft(null);
        await reload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [client, reload],
  );

  if (error) return <Banner tone="err">Could not load procedure citations: {error}</Banner>;
  if (!rows) return <Spinner label="Loading procedure citations…" />;

  return (
    <>
      <Card
        title={`Procedure Citations — ${filtered?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="users-filter">
            <SelectField
              label=""
              value={typeFilter}
              onChange={setTypeFilter}
              options={typeFilterOptions}
            />
            <TextField
              label=""
              value={filter}
              onChange={setFilter}
              placeholder="Filter by key, label, source, or author…"
            />
            <Button onClick={() => setDraft(blankDraft())}>New</Button>
          </div>
        }
      >
        <Banner tone="info">
          Canonical source-of-truth catalog for admin-config defaults. Every gate and admin default
          cites a row here (FK target for the NOT-NULL LegacyProcedureCitation column). Enforces
          CLAUDE.md rule #1 — do not guess about legacy procedure.
        </Banner>
        <DataTable<CitationRow>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => r.citationKey}
          emptyState={<p className="empty">No citations match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={
          draft ? (draft.isNew ? 'New procedure citation' : `Edit citation — ${draft.citationKey}`) : ''
        }
        onClose={() => (saving ? undefined : setDraft(null))}
        width={640}
        actions={
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save(draft)} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create citation' : 'Save'}
              </Button>
            </>
          ) : null
        }
      >
        {draft && (
          <>
            {saveErr && <Banner tone="err">{saveErr}</Banner>}
            <TextField
              label="Citation Key"
              value={draft.citationKey}
              onChange={(v) => setDraft({ ...draft, citationKey: v })}
              disabled={!draft.isNew}
              monospace
              required
              hint={
                draft.isNew
                  ? 'Stable PK (kebab/snake), e.g. project_trust_procedures_full_lock_20260506.'
                  : 'Read-only after create — it is the FK target other rows point at.'
              }
            />
            <SelectField
              label="Citation Type"
              value={draft.citationType}
              onChange={(v) => setDraft({ ...draft, citationType: v })}
              options={CITATION_TYPE_OPTIONS}
              required
            />
            <TextField
              label="Display Label"
              value={draft.displayLabel}
              onChange={(v) => setDraft({ ...draft, displayLabel: v })}
              required
              hint="Human-readable summary shown wherever this citation is referenced."
            />
            <TextField
              label="Source Path"
              value={draft.sourcePath}
              onChange={(v) => setDraft({ ...draft, sourcePath: v })}
              monospace
              hint="Memory file name, G:\Forms\* path, or Legacy Procedures PDF path."
            />
            <TextField
              label="Page Reference"
              value={draft.pageReference}
              onChange={(v) => setDraft({ ...draft, pageReference: v })}
              hint="PDFs only — e.g. p17 §4.2.1."
            />
            <TextField
              label="Author / Authority"
              value={draft.author}
              onChange={(v) => setDraft({ ...draft, author: v })}
              hint="Who established the source — Bill / Collette / Scott / Kent."
            />
            <TextField
              label="Source Date"
              value={draft.sourceDate}
              onChange={(v) => setDraft({ ...draft, sourceDate: v })}
              hint="When the source was created / captured (YYYY-MM-DD)."
            />
            <TextArea
              label="Notes"
              value={draft.notes}
              onChange={(v) => setDraft({ ...draft, notes: v })}
              rows={4}
              placeholder="What this citation establishes; surfaces on dependent rows."
            />
            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Show in the admin-config default picker."
            />
            <Banner tone="info">
              Reminder (CLAUDE.md rule #1): do not guess about legacy procedure. Every citation must
              reference a real source. External regulations alone are not acceptable — pair them with
              a memory-file or pdf-page citation documenting PCB&apos;s specific implementation.
            </Banner>
          </>
        )}
      </ModalShell>
    </>
  );
}
